/**
 * Portfolio Recommendations — AI-powered stock selection
 */
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, CircularProgress, Skeleton, alpha, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  LinearProgress,
} from '@mui/material';
import {
  Refresh, TrendingUp, TrendingDown, AccountBalance, Visibility,
  Category, Speed, WarningAmber,
  GppBad, GppMaybe, GppGood,
  Straighten, CandlestickChart, ShowChart, BoltOutlined,
  BatteryAlert, LocalFireDepartment, RemoveCircleOutline, TimerOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { portfolioAPI } from '../services/api';

// ── Helpers ──
const fmt = (v) => v ? new Intl.NumberFormat('vi-VN').format(v) : '–';
const scoreClr = (s) => s >= 80 ? '#10b981' : s >= 70 ? '#3b82f6' : s >= 60 ? '#f59e0b' : '#ef4444';
// entryClr removed — replaced by safetyLevelClr
const safetyLevelClr = (l) => l === 'SAFE' ? '#10b981' : l === 'MODERATE' ? '#3b82f6' : l === 'RISKY' ? '#f59e0b' : '#ef4444';
const getSector = (s, lang) => {
  if (!s) return '–';
  if (typeof s === 'string') return s;
  return lang === 'vi' ? (s.vi || '–') : (s.en || '–');
};

// ── Safety Detail Tooltip Content ──
const SafetyTooltipContent = ({ checks, stopLoss, t25Risk, close }) => {
  if (!checks || Object.keys(checks).length === 0) return null;
  const iconSx = { fontSize: 12, mr: 0.4, verticalAlign: 'middle' };
  const items = [
    { key: 'distance_ma20', label: 'Khoảng cách MA20', Icon: Straighten },
    { key: 'bollinger', label: 'Bollinger Band', Icon: CandlestickChart },
    { key: 'rsi', label: 'RSI', Icon: ShowChart },
    { key: 'volatility_spike', label: 'Biến động', Icon: BoltOutlined },
    { key: 'exhaustion', label: 'Kiệt sức', Icon: BatteryAlert },
    { key: 'overheating', label: 'Quá nhiệt', Icon: LocalFireDepartment },
  ];
  const checkClr = (score) => score >= 70 ? '#10b981' : score >= 40 ? '#f59e0b' : '#ef4444';
  return (
    <Box sx={{ p: 0.5, minWidth: 220, maxWidth: 280 }}>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#1e293b', mb: 0.8, borderBottom: '1px solid #e2e8f0', pb: 0.5 }}>
        Chi tiết độ an toàn
      </Typography>
      {items.map(({ key, label, Icon }) => {
        const c = checks[key];
        if (!c) return null;
        return (
          <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Icon sx={{ ...iconSx, color: '#94a3b8' }} />
              <Typography sx={{ fontSize: '0.68rem', color: '#64748b' }}>{label}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, fontFamily: 'monospace', color: checkClr(c.score) }}>{c.score?.toFixed(0)}</Typography>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: checkClr(c.score) }} />
            </Box>
          </Box>
        );
      })}
      {(stopLoss > 0 || t25Risk > 0) && (
        <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid #e2e8f0' }}>
          {stopLoss > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <RemoveCircleOutline sx={{ ...iconSx, color: '#ef4444' }} />
                <Typography sx={{ fontSize: '0.68rem', color: '#64748b' }}>Stop-loss</Typography>
              </Box>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, fontFamily: 'monospace', color: '#ef4444' }}>{fmt(stopLoss)} ({close > 0 ? ((1 - stopLoss / close) * 100).toFixed(1) : 0}%)</Typography>
            </Box>
          )}
          {t25Risk > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TimerOutlined sx={{ ...iconSx, color: t25Risk > 8 ? '#ef4444' : t25Risk > 5 ? '#f59e0b' : '#10b981' }} />
                <Typography sx={{ fontSize: '0.68rem', color: '#64748b' }}>Rủi ro T+2.5</Typography>
              </Box>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, fontFamily: 'monospace', color: t25Risk > 8 ? '#ef4444' : t25Risk > 5 ? '#f59e0b' : '#10b981' }}>{t25Risk?.toFixed(1)}%</Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

// ── Safety Badge (compact) ──
const SafetyBadge = ({ score, level, checks, stopLoss, t25Risk, close }) => {
  const clr = safetyLevelClr(level);
  const Icon = level === 'SAFE' ? GppGood : level === 'MODERATE' ? GppMaybe : GppBad;
  return (
    <Tooltip
      title={<SafetyTooltipContent checks={checks} stopLoss={stopLoss} t25Risk={t25Risk} close={close} />}
      arrow placement="left"
      slotProps={{ tooltip: { sx: { bgcolor: '#fff', color: '#1e293b', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', borderRadius: 1.5 } } }}
    >
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, cursor: 'pointer' }}>
        <Icon sx={{ fontSize: 15, color: clr }} />
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace', color: clr }}>{score?.toFixed(0)}</Typography>
      </Box>
    </Tooltip>
  );
};

// ── Signal badge ──
const SignalBadge = ({ signal, t }) => {
  const cfg = {
    BUY_NOW: { color: '#10b981', label: t('portfolioRecommendations.buyNow', 'Mua ngay') },
    BUY_DIP: { color: '#3b82f6', label: t('portfolioRecommendations.buyDip', 'Mua thêm') },
    WAIT_PULLBACK: { color: '#f59e0b', label: t('portfolioRecommendations.waitPullback', 'Chờ hồi') },
    WAIT: { color: '#94a3b8', label: t('portfolioRecommendations.wait', 'Theo dõi') },
  };
  const c = cfg[signal] || cfg.WAIT;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, px: 0.8, py: 0.15, borderRadius: 0.8, bgcolor: alpha(c.color, 0.08) }}>
      <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: c.color }} />
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: c.color, whiteSpace: 'nowrap' }}>{c.label}</Typography>
    </Box>
  );
};

// ── Score bar mini ──
const ScoreBar = ({ value, color }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace', color, minWidth: 24, textAlign: 'right' }}>
      {value?.toFixed(0)}
    </Typography>
    <LinearProgress
      variant="determinate" value={Math.min((value / 100) * 100, 100)}
      sx={{ flex: 1, height: 4, borderRadius: 1, bgcolor: '#f1f5f9', maxWidth: 50,
        '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 1 } }}
    />
  </Box>
);

// ── Stat Tile ──
const Tile = ({ label, value, sub, color, Icon }) => (
  <Box sx={{
    position: 'relative', overflow: 'hidden',
    borderRadius: 2.5, border: `1px solid ${alpha(color, 0.15)}`,
    bgcolor: '#fff', p: '14px 16px', transition: 'all 0.2s',
    '&:hover': { borderColor: alpha(color, 0.3), boxShadow: `0 4px 16px ${alpha(color, 0.08)}`, transform: 'translateY(-1px)' },
  }}>
    <Box sx={{ position: 'absolute', top: 0, right: 0, width: 64, height: 64, background: `radial-gradient(circle at top right, ${alpha(color, 0.06)} 0%, transparent 70%)` }} />
    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Box sx={{
        width: 38, height: 38, borderRadius: 2, flexShrink: 0,
        background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: `0 3px 8px ${alpha(color, 0.25)}`,
      }}>
        <Icon sx={{ fontSize: 20, color: '#fff' }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8', lineHeight: 1, mb: 0.5 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: typeof value === 'string' && value.length > 8 ? '1rem' : '1.75rem', fontWeight: 900, color, lineHeight: 1.2, fontFamily: typeof value === 'number' ? 'monospace' : 'inherit' }}>
          {value}
        </Typography>
        {sub && <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', mt: 0.4 }}>{sub}</Typography>}
      </Box>
    </Box>
  </Box>
);

// ── Empty State ──
const EmptyState = ({ Icon, title, subtitle }) => (
  <Box sx={{ py: 5, px: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
    <Box sx={{
      width: 56, height: 56, borderRadius: '50%',
      bgcolor: '#f8fafc', border: '1.5px dashed #e2e8f0',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {Icon && <Icon sx={{ fontSize: 24, color: '#cbd5e1' }} />}
    </Box>
    <Box sx={{ textAlign: 'center' }}>
      <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: '#94a3b8', lineHeight: 1.4 }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontSize: '0.72rem', color: '#cbd5e1', mt: 0.3 }}>{subtitle}</Typography>}
    </Box>
  </Box>
);

// ── Table styles ──
const thSx = { fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.02em', py: 0.8, borderBottom: '1px solid #e2e8f0' };
const tdSx = { py: 0.7, fontSize: '0.8rem', borderBottom: '1px solid #f1f5f9' };

// ── Stock Table ──
const StockTable = ({ data, columns, lang, t, emptyIcon: EIcon, emptyText, emptySub }) => {
  if (!data || data.length === 0) {
    return <EmptyState Icon={EIcon} title={emptyText} subtitle={emptySub} />;
  }
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((c, i) => (
              <TableCell key={i} align={c.align || 'left'} sx={thSx}>{c.label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((s, idx) => (
            <TableRow key={s.ticker || idx} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
              {columns.map((c, i) => (
                <TableCell key={i} align={c.align || 'left'} sx={tdSx}>
                  {c.render(s, idx, lang, t)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// ── Sectors Table ──
const SectorsTable = ({ sectors, lang }) => {
  if (!sectors || sectors.length === 0) {
    return <EmptyState Icon={Category} title="Chưa có dữ liệu ngành" subtitle="Dữ liệu sẽ cập nhật sau phiên giao dịch" />;
  }
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...thSx, width: 40 }}>#</TableCell>
            <TableCell sx={thSx}>Sector</TableCell>
            <TableCell align="right" sx={thSx}>Score</TableCell>
            <TableCell sx={{ ...thSx, minWidth: 140 }}>Strength</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sectors.map((s, i) => (
            <TableRow key={i} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
              <TableCell sx={{ ...tdSx, color: '#94a3b8', fontFamily: 'monospace', fontSize: '0.75rem' }}>{i + 1}</TableCell>
              <TableCell sx={{ ...tdSx, fontWeight: 700, fontSize: '0.82rem' }}>{getSector(s.sector, lang)}</TableCell>
              <TableCell align="right" sx={{ ...tdSx, fontWeight: 700, fontFamily: 'monospace', color: scoreClr(s.score) }}>
                {s.score?.toFixed(1)}
              </TableCell>
              <TableCell sx={tdSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinearProgress
                    variant="determinate" value={Math.min(s.score, 100)}
                    sx={{ flex: 1, height: 5, borderRadius: 1, bgcolor: '#f1f5f9',
                      '& .MuiLinearProgress-bar': { bgcolor: s.score > 70 ? '#10b981' : s.score > 40 ? '#3b82f6' : '#ef4444', borderRadius: 1 } }}
                  />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, fontFamily: 'monospace', color: '#64748b', minWidth: 28, textAlign: 'right' }}>
                    {s.score?.toFixed(0)}
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// ── Loading skeleton ──
const PageSkeleton = () => (
  <Box sx={{ p: 2.5, maxWidth: 1400, mx: 'auto' }}>
    {/* Header */}
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Skeleton variant="rounded" width={42} height={42} sx={{ borderRadius: 2.5, flexShrink: 0 }} />
        <Box>
          <Skeleton width={220} height={28} animation="wave" />
          <Skeleton width={180} height={16} animation="wave" sx={{ mt: 0.3 }} />
        </Box>
      </Box>
      <Skeleton variant="rounded" width={90} height={34} sx={{ borderRadius: 2 }} animation="wave" />
    </Box>

    {/* Stat tiles */}
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5, mb: 2 }}>
      {[0, 1, 2, 3].map(i => (
        <Box key={i} sx={{ borderRadius: 2.5, border: '1px solid #e2e8f0', bgcolor: '#fff', p: '14px 16px' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <Skeleton variant="rounded" width={38} height={38} sx={{ borderRadius: 2, flexShrink: 0 }} animation="wave" />
            <Box sx={{ flex: 1 }}>
              <Skeleton width={60} height={12} animation="wave" sx={{ mb: 0.8 }} />
              <Skeleton width={40} height={26} animation="wave" sx={{ mb: 0.4 }} />
              <Skeleton width={100} height={11} animation="wave" />
            </Box>
          </Box>
        </Box>
      ))}
    </Box>

    {/* Tab bar */}
    <Box sx={{ display: 'flex', gap: 0, borderBottom: '2px solid #e2e8f0', mb: 0 }}>
      {[90, 75, 70, 60].map((w, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.8, px: 2.5, py: 1.2, borderBottom: i === 0 ? '2.5px solid #cbd5e1' : '2.5px solid transparent', mb: '-2px' }}>
          <Skeleton variant="circular" width={18} height={18} animation="wave" />
          <Skeleton width={w} height={16} animation="wave" />
          <Skeleton variant="rounded" width={24} height={18} animation="wave" sx={{ borderRadius: 1 }} />
        </Box>
      ))}
    </Box>

    {/* Table content */}
    <Box sx={{ borderRadius: '0 0 10px 10px', border: '1px solid #e2e8f0', borderTop: 'none', bgcolor: '#fff', overflow: 'hidden' }}>
      {/* Column headers */}
      <Box sx={{ display: 'flex', gap: 2, px: 2, py: 1, borderBottom: '1px solid #e2e8f0' }}>
        {[20, 40, 60, 50, 50, 50, 45, 50, 65].map((w, i) => (
          <Skeleton key={i} width={w} height={12} animation="wave" />
        ))}
      </Box>
      {/* Rows */}
      {[...Array(8)].map((_, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 0.8, borderBottom: i < 7 ? '1px solid #f1f5f9' : 'none' }}>
          <Skeleton width={14} height={14} animation="wave" />
          <Skeleton width={42} height={16} animation="wave" sx={{ fontWeight: 700 }} />
          <Skeleton width={70} height={14} animation="wave" sx={{ flex: 1 }} />
          <Skeleton width={38} height={14} animation="wave" />
          <Skeleton width={32} height={14} animation="wave" />
          <Skeleton width={18} height={14} animation="wave" />
          <Skeleton width={42} height={14} animation="wave" />
          <Skeleton width={48} height={14} animation="wave" />
          <Skeleton variant="rounded" width={56} height={20} animation="wave" sx={{ borderRadius: 0.8 }} />
        </Box>
      ))}
    </Box>
  </Box>
);

// ══════════════════════════════════════
// ── Main Component ──
// ══════════════════════════════════════
function PortfolioRecommendations() {
  const { i18n, t } = useTranslation();
  const lang = i18n.language;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async () => {
    try {
      setRefreshing(true); setError(null);
      const resp = await portfolioAPI.getRecommendations({ top_sectors: 6, stocks_per_sector: 4 });
      setData(resp.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line

  if (loading) return <PageSkeleton />;

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#ef4444', 0.06), border: `1px solid ${alpha('#ef4444', 0.15)}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontSize: '0.85rem', color: '#ef4444' }}>{error}</Typography>
          <Button size="small" onClick={fetchData} sx={{ textTransform: 'none', fontWeight: 700, color: '#ef4444' }}>
            {t('portfolioRecommendations.retry', 'Thử lại')}
          </Button>
        </Box>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 3 }}>
        <EmptyState Icon={WarningAmber} title={t('portfolioRecommendations.noData', 'Không có dữ liệu')} />
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button size="small" onClick={fetchData} startIcon={<Refresh />} sx={{ textTransform: 'none', fontWeight: 700 }}>
            {t('portfolioRecommendations.refresh', 'Làm mới')}
          </Button>
        </Box>
      </Box>
    );
  }

  // ── Extract from actual API response ──
  // API format: { date, market, portfolio: [...], watchlist: [...], sectors: [...], alerts: [...], orders_plan, ... }
  const { market, portfolio, watchlist, sectors, alerts, date: apiDate } = data;

  const buyNow = portfolio?.filter(s => s.state === 'BUY_NOW') || [];
  const waitStocks = [
    ...(portfolio?.filter(s => s.state !== 'BUY_NOW') || []),
    ...(watchlist || []),
  ];
  const highAlerts = alerts?.filter(a => a.urgency === 'HIGH').length || 0;
  const avgScore = portfolio?.length > 0
    ? (portfolio.reduce((sum, s) => sum + (s.FinalScore || 0), 0) / portfolio.length).toFixed(1) : '–';
  const uniqueSectors = [...new Set(portfolio?.map(s => getSector(s.sector, lang)) || [])].length;

  // ── Regime display ──
  const regimeCfg = {
    RISK_ON: { color: '#10b981', label: 'Risk On', Icon: TrendingUp },
    RECOVERY: { color: '#3b82f6', label: 'Recovery', Icon: TrendingUp },
    SIDEWAY: { color: '#f59e0b', label: 'Sideway', Icon: Speed },
    NEUTRAL: { color: '#3b82f6', label: 'Neutral', Icon: Speed },
    RISK_OFF: { color: '#ef4444', label: 'Risk Off', Icon: TrendingDown },
  };
  const rc = regimeCfg[market?.regime] || regimeCfg.NEUTRAL;

  // ── Column definitions — matching actual API field names ──
  const portfolioCols = [
    { label: '#', align: 'center', render: (_, i) => <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontFamily: 'monospace' }}>{i + 1}</Typography> },
    { label: t('portfolioRecommendations.table.stock', 'Mã'), render: (s) => <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: '#1e293b' }}>{s.ticker}</Typography> },
    { label: t('portfolioRecommendations.table.sector', 'Ngành'), render: (s, _, l) => (
      <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
        {getSector(s.sector, l)}
      </Typography>
    )},
    { label: t('portfolioRecommendations.table.weight', 'Tỷ trọng'), align: 'right', render: (s) => (
      <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: s.weight > 0 ? '#3b82f6' : '#94a3b8', fontFamily: 'monospace' }}>
        {(s.weight * 100).toFixed(1)}%
      </Typography>
    )},
    { label: t('portfolioRecommendations.table.score', 'Điểm'), align: 'right', render: (s) => <ScoreBar value={s.FinalScore} color={scoreClr(s.FinalScore)} /> },
    { label: 'An toàn', align: 'right', render: (s) => (
      <SafetyBadge score={s.EntrySafety} level={s.SafetyLevel} checks={s.SafetyChecks} stopLoss={s.StopLoss} t25Risk={s.T25Risk} close={s.close} />
    )},
    { label: 'Stop-loss', align: 'right', render: (s) => (
      <Box>
        <Typography sx={{ fontSize: '0.78rem', fontFamily: 'monospace', fontWeight: 600, color: '#ef4444' }}>{s.StopLoss > 0 ? fmt(s.StopLoss) : '–'}</Typography>
        {s.StopLossRisk > 0 && <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', fontFamily: 'monospace' }}>-{s.StopLossRisk}%</Typography>}
      </Box>
    )},
    { label: t('portfolioRecommendations.table.price', 'Giá'), align: 'right', render: (s) => <Typography sx={{ fontSize: '0.82rem', fontFamily: 'monospace', fontWeight: 600 }}>{fmt(s.close)}</Typography> },
    { label: t('portfolioRecommendations.table.state', 'Tín hiệu'), render: (s, _, __, tr) => <SignalBadge signal={s.state} t={tr} /> },
  ];

  const buyNowCols = [
    { label: '#', align: 'center', render: (_, i) => <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontFamily: 'monospace' }}>{i + 1}</Typography> },
    { label: t('portfolioRecommendations.table.stock', 'Mã'), render: (s) => <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: '#1e293b' }}>{s.ticker}</Typography> },
    { label: t('portfolioRecommendations.table.sector', 'Ngành'), render: (s, _, l) => (
      <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
        {getSector(s.sector, l)}
      </Typography>
    )},
    { label: t('portfolioRecommendations.table.weight', 'Tỷ trọng'), align: 'right', render: (s) => <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#3b82f6', fontFamily: 'monospace' }}>{(s.weight * 100).toFixed(1)}%</Typography> },
    { label: 'An toàn', align: 'right', render: (s) => (
      <SafetyBadge score={s.EntrySafety} level={s.SafetyLevel} checks={s.SafetyChecks} stopLoss={s.StopLoss} t25Risk={s.T25Risk} close={s.close} />
    )},
    { label: 'Stop-loss', align: 'right', render: (s) => (
      <Box>
        <Typography sx={{ fontSize: '0.78rem', fontFamily: 'monospace', fontWeight: 600, color: '#ef4444' }}>{s.StopLoss > 0 ? fmt(s.StopLoss) : '–'}</Typography>
        {s.StopLossRisk > 0 && <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', fontFamily: 'monospace' }}>-{s.StopLossRisk}%</Typography>}
      </Box>
    )},
    { label: 'T+2.5', align: 'right', render: (s) => (
      <Typography sx={{ fontSize: '0.78rem', fontFamily: 'monospace', fontWeight: 600, color: s.T25Risk > 8 ? '#ef4444' : s.T25Risk > 5 ? '#f59e0b' : '#10b981' }}>
        {s.T25Risk > 0 ? `${s.T25Risk?.toFixed(1)}%` : '–'}
      </Typography>
    )},
    { label: t('portfolioRecommendations.table.price', 'Giá'), align: 'right', render: (s) => <Typography sx={{ fontSize: '0.82rem', fontFamily: 'monospace', fontWeight: 600 }}>{fmt(s.close)}</Typography> },
    { label: 'RSI', align: 'right', render: (s) => (
      <Typography sx={{ fontSize: '0.78rem', fontFamily: 'monospace', fontWeight: 600, color: s.rsi14 > 70 ? '#ef4444' : s.rsi14 < 30 ? '#10b981' : '#64748b' }}>
        {s.rsi14?.toFixed(0)}
      </Typography>
    )},
  ];

  const waitCols = [
    { label: '#', align: 'center', render: (_, i) => <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontFamily: 'monospace' }}>{i + 1}</Typography> },
    { label: t('portfolioRecommendations.table.stock', 'Mã'), render: (s) => <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: '#1e293b' }}>{s.ticker}</Typography> },
    { label: t('portfolioRecommendations.table.sector', 'Ngành'), render: (s, _, l) => (
      <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
        {getSector(s.sector, l)}
      </Typography>
    )},
    { label: t('portfolioRecommendations.table.score', 'Điểm'), align: 'right', render: (s) => <ScoreBar value={s.FinalScore} color={'#f59e0b'} /> },
    { label: 'An toàn', align: 'right', render: (s) => (
      <SafetyBadge score={s.EntrySafety} level={s.SafetyLevel} checks={s.SafetyChecks} stopLoss={s.StopLoss} t25Risk={s.T25Risk} close={s.close} />
    )},
    { label: 'Stop-loss', align: 'right', render: (s) => (
      <Typography sx={{ fontSize: '0.78rem', fontFamily: 'monospace', fontWeight: 600, color: '#ef4444' }}>{s.StopLoss > 0 ? fmt(s.StopLoss) : '–'}</Typography>
    )},
    { label: t('portfolioRecommendations.table.price', 'Giá'), align: 'right', render: (s) => <Typography sx={{ fontSize: '0.82rem', fontFamily: 'monospace', fontWeight: 600 }}>{fmt(s.close)}</Typography> },
    { label: t('portfolioRecommendations.table.state', 'Tín hiệu'), render: (s, _, __, tr) => <SignalBadge signal={s.state || s.MultiTFSignal} t={tr} /> },
  ];

  const TABS = [
    { key: 'portfolio', label: t('portfolioRecommendations.tabs.portfolio', 'Danh mục'), color: '#3b82f6', Icon: AccountBalance, count: portfolio?.length || 0 },
    { key: 'buy', label: t('portfolioRecommendations.tabs.buyNow', 'Mua ngay'), color: '#10b981', Icon: TrendingUp, count: buyNow.length },
    { key: 'wait', label: t('portfolioRecommendations.tabs.wait', 'Chờ hồi'), color: '#f59e0b', Icon: Visibility, count: waitStocks.length },
    { key: 'sectors', label: t('portfolioRecommendations.tabs.sectors', 'Ngành'), color: '#8b5cf6', Icon: Category, count: sectors?.length || 0 },
  ];

  return (
    <Box sx={{ p: 2.5, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 2.5, flexShrink: 0,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 3px 10px ${alpha('#667eea', 0.3)}`,
          }}>
            <AccountBalance sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', lineHeight: 1.2 }}>
              {t('portfolioRecommendations.title', 'Khuyến Nghị Danh Mục')}
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 500, lineHeight: 1.3, mt: 0.3 }}>
              {t('portfolioRecommendations.subtitle', 'Đề xuất cổ phiếu dựa trên phân tích AI')}
              {apiDate && <Typography component="span" sx={{ fontSize: '0.75rem', color: '#94a3b8', ml: 1 }}>· {apiDate}</Typography>}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained" size="small"
          startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : <Refresh sx={{ fontSize: 18 }} />}
          onClick={fetchData} disabled={refreshing}
          sx={{
            px: 2.5, py: 0.8, fontSize: '0.85rem', fontWeight: 700,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            boxShadow: `0 3px 8px ${alpha('#667eea', 0.3)}`,
            textTransform: 'none', borderRadius: 2,
            '&:hover': { boxShadow: `0 4px 14px ${alpha('#667eea', 0.4)}` },
          }}
        >
          {refreshing ? t('portfolioRecommendations.refreshing', 'Đang tải...') : t('portfolioRecommendations.refresh', 'Làm mới')}
        </Button>
      </Box>

      {/* Stat tiles */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5, mb: 2 }}>
        <Tile
          label={t('portfolioRecommendations.marketRegime', 'Chế độ thị trường')}
          value={rc.label}
          sub={`Trend: ${market?.trend || '–'} · Vol: ${market?.volatility || '–'}`}
          color={rc.color} Icon={rc.Icon}
        />
        <Tile
          label={t('portfolioRecommendations.portfolio', 'Danh mục')}
          value={portfolio?.length || 0}
          sub={`${t('portfolioRecommendations.avgScore', 'TB')}: ${avgScore} · ${uniqueSectors} ${t('portfolioRecommendations.sectors', 'ngành').toLowerCase()}`}
          color="#3b82f6" Icon={AccountBalance}
        />
        <Tile
          label={t('portfolioRecommendations.tabs.buyNow', 'Mua ngay')}
          value={buyNow.length}
          sub={`${(buyNow.reduce((s, h) => s + (h.weight || 0), 0) * 100).toFixed(0)}% ${t('portfolioRecommendations.allocated', 'đã phân bổ')}`}
          color="#10b981" Icon={TrendingUp}
        />
        <Tile
          label={t('portfolioRecommendations.tabs.wait', 'Chờ hồi')}
          value={waitStocks.length}
          sub={highAlerts > 0 ? `${highAlerts} cảnh báo cao` : t('portfolioRecommendations.noAlerts', 'Không có cảnh báo')}
          color="#f59e0b" Icon={Visibility}
        />
      </Box>

      {/* Tab bar */}
      <Box sx={{ display: 'flex', gap: 0, borderBottom: '2px solid #e2e8f0', mb: 0 }}>
        {TABS.map((tab, idx) => {
          const active = activeTab === idx;
          return (
            <Box
              key={tab.key}
              onClick={() => setActiveTab(idx)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.8, px: 2.5, py: 1.2, cursor: 'pointer',
                borderBottom: active ? `2.5px solid ${tab.color}` : '2.5px solid transparent', mb: '-2px',
                transition: 'all 0.15s', '&:hover': { bgcolor: alpha(tab.color, 0.04) },
              }}
            >
              <tab.Icon sx={{ fontSize: 18, color: active ? tab.color : '#94a3b8' }} />
              <Typography sx={{ fontSize: '0.85rem', fontWeight: active ? 800 : 600, color: active ? tab.color : '#64748b' }}>
                {tab.label}
              </Typography>
              <Box sx={{
                display: 'inline-flex', px: 0.8, py: 0.15, borderRadius: 1,
                bgcolor: active ? alpha(tab.color, 0.1) : '#f1f5f9',
                minWidth: 24, justifyContent: 'center',
              }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, fontFamily: 'monospace', color: active ? tab.color : '#94a3b8' }}>
                  {tab.count}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Tab content */}
      <Box sx={{ borderRadius: '0 0 10px 10px', border: '1px solid #e2e8f0', borderTop: 'none', bgcolor: '#fff', overflow: 'hidden' }}>
        {activeTab === 0 && (
          <StockTable data={portfolio} columns={portfolioCols} lang={lang} t={t}
            emptyIcon={AccountBalance}
            emptyText={t('portfolioRecommendations.empty.portfolio', 'Chưa có cổ phiếu trong danh mục')}
            emptySub={t('portfolioRecommendations.empty.portfolioSub', 'Hệ thống sẽ đề xuất khi có tín hiệu phù hợp')}
          />
        )}
        {activeTab === 1 && (
          <StockTable data={buyNow} columns={buyNowCols} lang={lang} t={t}
            emptyIcon={TrendingUp}
            emptyText={t('portfolioRecommendations.empty.buyNow', 'Hiện chưa có tín hiệu mua ngay')}
            emptySub={t('portfolioRecommendations.empty.buyNowSub', 'Các ứng viên sẽ xuất hiện khi đủ điều kiện entry')}
          />
        )}
        {activeTab === 2 && (
          <StockTable data={waitStocks} columns={waitCols} lang={lang} t={t}
            emptyIcon={Visibility}
            emptyText={t('portfolioRecommendations.empty.wait', 'Không có cổ phiếu đang chờ')}
            emptySub={t('portfolioRecommendations.empty.waitSub', 'Danh sách chờ sẽ hiện khi có mã tiềm năng chưa đủ điều kiện')}
          />
        )}
        {activeTab === 3 && <SectorsTable sectors={sectors} lang={lang} />}
      </Box>
    </Box>
  );
}

export default PortfolioRecommendations;
